const LEGACY_PLAN_SELECTOR_MODE = 'modelVariantCustomerLine';
const LINE_PART_MOLD_CUSTOMER_MODE = 'linePartMoldCustomer';

const getAssetId = (record) => record?.assetid ?? record?.assetId ?? null;

const getUniqueRecords = (records, key) => [...new Map(
  records
    .filter((record) => record && record[key] !== undefined && record[key] !== null && record[key] !== '')
    .map((record) => [record[key], record]),
).values()];

const createMachineDetail = ({
  machinename,
  stdcycletime,
  linename,
  delaytime,
  cavity,
}) => ({
  machinename,
  stdcycletime,
  linename,
  delaytime,
  activecavity: cavity,
});

const normalizeNetweight = (value) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  const parsedValue = Number(value);

  return Number.isNaN(parsedValue)
    ? value
    : parsedValue;
};

export const resolvePartNetweight = ({
  parts = [],
  partname = '',
  selectedPart = null,
  fallback = '',
}) => {
  const normalizedPartName = String(partname || '').trim();

  if (normalizedPartName) {
    const masterPart = (parts || []).find((part) => (
      String(part.partname || '').trim() === normalizedPartName
    ));

    const masterNetweight = normalizeNetweight(masterPart && masterPart.netweight);
    if (masterNetweight !== '') {
      return masterNetweight;
    }
  }

  const selectedNetweight = normalizeNetweight(selectedPart && selectedPart.netweight);
  if (selectedNetweight !== '') {
    return selectedNetweight;
  }

  return fallback;
};

const getPlanningSchema = (store) => {
  const appSchema = store.state.webApp.appSchema || [];
  const licensedAssets = store.getters['user/licensedAssets'] || [];
  const licensedAssetIds = licensedAssets.map(String);
  return appSchema.filter((schema) => licensedAssetIds.includes(String(schema.assetId)));
};

const resolvePlanSelectorMode = (planningSchema) => {
  const schemaWithSelectorMode = planningSchema.find(
    (schema) => schema.planSelectorMode || schema.planSelectionMode,
  );
  if (schemaWithSelectorMode) {
    return schemaWithSelectorMode.planSelectorMode
      || schemaWithSelectorMode.planSelectionMode;
  }

  const moldBasedSchema = planningSchema.find((schema) => {
    const composition = (schema.partMatrixComposition || []).map((value) => value.toLowerCase());
    return composition.includes('mold') || composition.includes('tool');
  });
  if (moldBasedSchema) {
    return LINE_PART_MOLD_CUSTOMER_MODE;
  }

  return LEGACY_PLAN_SELECTOR_MODE;
};

export const fetchPartMatrixRecords = async (store) => {
  const records = await store.dispatch(
    'element/getRecords',
    {
      elementName: 'partmatrix',
    },
    { root: true },
  );

  if (records && records.length) {
    return records.map((matrix) => ({
      ...matrix,
      equipmentname: matrix.moldname || matrix.toolname || '-',
    }));
  }

  return [];
};

export const fetchMachineRecords = async (store) => {
  const existingMachines = store?.state?.assemblyPlanning?.machines || [];
  if (existingMachines.length) {
    return existingMachines;
  }

  const records = await store.dispatch(
    'element/getRecords',
    {
      elementName: 'machine',
    },
    { root: true },
  );

  return records || [];
};

export const buildPlanMachineDetails = ({
  selectedMatrix,
  machines,
  lineName,
  assetId = null,
}) => {
  if (!selectedMatrix || !selectedMatrix.length) {
    return [];
  }

  const [defaultMatrix] = selectedMatrix;
  const selectedAssetId = getAssetId(defaultMatrix);
  const lineMachines = getUniqueRecords(
    (machines || []).filter((machine) => machine.linename === lineName),
    'machinename',
  ).sort((left, right) => left.machinename.localeCompare(right.machinename));

  const machineAssetIds = [...new Set(
    lineMachines
      .map((machine) => getAssetId(machine))
      .filter((value) => value !== null && value !== undefined && value !== ''),
  )];
  const effectiveAssetId = assetId
    || (machineAssetIds.length === 1 ? machineAssetIds[0] : null)
    || selectedAssetId;
  const filteredMachines = effectiveAssetId === null
    ? lineMachines
    : lineMachines.filter((machine) => {
      const machineAssetId = getAssetId(machine);
      return machineAssetId !== null
        && String(machineAssetId) === String(effectiveAssetId);
    });

  if (!filteredMachines.length) {
    return selectedMatrix.map((matrix) => createMachineDetail(matrix));
  }

  const matrixByMachine = new Map(
    selectedMatrix
      .filter((matrix) => matrix.machinename)
      .map((matrix) => [matrix.machinename, matrix]),
  );

  return filteredMachines.map((machine) => {
    const machineMatrix = matrixByMachine.get(machine.machinename) || defaultMatrix;
    return createMachineDetail({
      machinename: machine.machinename,
      stdcycletime: machineMatrix.stdcycletime,
      linename: machine.linename || lineName || machineMatrix.linename,
      delaytime: machineMatrix.delaytime,
      cavity: machineMatrix.cavity,
    });
  });
};

export default {
  computed: {
    planSelectorMode() {
      return resolvePlanSelectorMode(getPlanningSchema(this.$store));
    },
    isLinePartMoldCustomerMode() {
      return this.planSelectorMode === LINE_PART_MOLD_CUSTOMER_MODE;
    },
    isShowColor() {
      const planningSchema = getPlanningSchema(this.$store);
      return planningSchema.some((schema) => schema.isShowColor);
    },
    isPlanImportEnabled() {
      return true;
    },
  },
};
